// ManagePayroll.js
import React, { useState, useEffect, useMemo, useCallback  } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Link,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Skeleton,
  Alert,
  useMediaQuery,
  useTheme,
  Fade,
  Tooltip,
  IconButton,
  InputAdornment,
  LinearProgress,
  TableSortLabel,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  GetApp as ExportIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Update as UpdateIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { getPayrolls, searchPayrolls, prefetchPayrollById, deletePayroll, savePayroll, updatePayrollInfo, getAllPayrollIdsByStores, invalidatePayrollCache } from '../../api/payroll'
import { getStoredUser, getStoredToken } from '../../api/client';
import { getApprovedBreaklists } from '../../api/timekeep';
import { getAllowances, assignAllowancesToPayrolls, clearAllowancesFromPayrolls } from '../../api/allowances';
import { listStores } from '../../api/stores';
import { getRates } from '../../api/rates';
import { listGroups, listGroupStores } from '../../api/groups';
import DateRangeModal from "../../components/DateRangeModal";
import Swal from "sweetalert2";
import axios from 'axios';
import jsPDF from "jspdf";
import "jspdf-autotable";
import { exportToPrint, formatDataForExport } from '../../utils/exportUtils';
// Set default base URL
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

// Mock employees (replace with API)
const employees = [
  { id: 1, name: 'Juan Dela Cruz', hours: 80, rate: 100, amount: 8000 },
  { id: 2, name: 'Maria Santos', hours: 76, rate: 120, amount: 9120 },
  { id: 3, name: 'Pedro Reyes', hours: 90, rate: 95, amount: 8550 },
];

// Global state for processPayrolls function
let globalSetLoading, globalSetProgress, globalEnqueueSnackbar;

// Process payrolls function that can be called from other components
export const exportToPDF = async (data, filename, title, headers = null) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });


    // Prepare table data
    const tableHeaders = headers || Object.keys(data[0]);
    const tableData = data.map(row =>
      tableHeaders.map(header => {
        const value = row[header];
        return value !== null && value !== undefined ? String(value) : '';
      })
    );

    // Split table into chunks that fit on page width
    const splitTableIntoChunks = (headers, data) => {
      const pageWidth = doc.internal.pageSize.width - 20; // Account for margins
      const minColumnWidth = 18; // Minimum readable width
      const maxColumnsPerPage = Math.floor(pageWidth / minColumnWidth);
      
      console.log(`Page width: ${pageWidth}mm, Min column width: ${minColumnWidth}mm, Max columns per page: ${maxColumnsPerPage}`);
      console.log(`Total columns: ${headers.length}`);
      
      // If we have more columns than can fit on one page, split into chunks
      if (headers.length > maxColumnsPerPage) {
        const chunks = [];
        for (let i = 0; i < headers.length; i += maxColumnsPerPage) {
          const endIndex = Math.min(i + maxColumnsPerPage, headers.length);
          const chunkHeaders = headers.slice(i, endIndex);
          const chunkData = data.map(row => 
            chunkHeaders.map((_, index) => row[i + index])
          );
          chunks.push({
            headers: chunkHeaders,
            data: chunkData,
            startIndex: i,
            endIndex: endIndex - 1
          });
          console.log(`Chunk ${chunks.length}: columns ${i + 1}-${endIndex} (${chunkHeaders.length} columns)`);
        }
        return chunks;
      }
      
      // If all columns fit on one page, return as single chunk
      console.log('All columns fit on one page');
      return [{
        headers: headers,
        data: data,
        startIndex: 0,
        endIndex: headers.length - 1
      }];
    };

    const tableChunks = splitTableIntoChunks(tableHeaders, tableData);

    // Generate tables for each chunk
    tableChunks.forEach((chunk, chunkIndex) => {
      // Add title and timestamp for each chunk
      if (chunkIndex === 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(title, 10, 20);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 10, 28);
      } else {
        // Add new page for additional chunks
        doc.addPage();
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`${title} (Part ${chunkIndex + 1})`, 10, 20);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Columns ${chunk.startIndex + 1}-${chunk.endIndex + 1}`, 10, 28);
      }

      // Calculate optimal column widths for this chunk
      const pageWidth = doc.internal.pageSize.width - 20;
      const columnWidth = Math.max(18, pageWidth / chunk.headers.length);
      
      console.log(`Chunk ${chunkIndex + 1}: ${chunk.headers.length} columns, column width: ${columnWidth}mm`);

      doc.autoTable({
        head: [chunk.headers],
        body: chunk.data,
        startY: chunkIndex === 0 ? 35 : 35,
        styles: {
          fontSize: 7,
          cellPadding: 2,
          lineWidth: 0.1,
          lineColor: [200, 200, 200],
          overflow: 'linebreak',
          halign: 'left',
          valign: 'top',
          textColor: [0, 0, 0]
        },
        headStyles: {
          fillColor: [18, 193, 220], // Your theme color #12C1DC
          textColor: 255,
          fontSize: 7,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          overflow: 'linebreak'
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        },
        margin: { top: chunkIndex === 0 ? 35 : 35, left: 10, right: 10 },
        theme: 'grid',
        columnStyles: Object.fromEntries(
          chunk.headers.map((_, index) => [
            index,
            { 
              cellWidth: columnWidth,
              overflow: 'linebreak',
              halign: 'left',
              valign: 'top'
            }
          ])
        ),
        didParseCell: (data) => {
          // Handle specific column types
          if (data.column.index === 0 || data.column.index === 1) {
            // ID and Name columns
            data.cell.styles.halign = 'center';
            data.cell.styles.overflow = 'linebreak';
          }
          
          // Handle long text
          if (data.cell.raw && typeof data.cell.raw === 'string') {
            const textLength = data.cell.raw.length;
            data.cell.styles.overflow = 'linebreak';
            
            if (textLength > 20) {
              data.cell.styles.fontSize = 6;
            }
            if (textLength > 40) {
              data.cell.styles.fontSize = 5;
            }
          }
          
          // Handle numeric columns
          if (typeof data.cell.raw === 'number' || /^\d+\.?\d*$/.test(String(data.cell.raw))) {
            data.cell.styles.halign = 'right';
          }
          
          data.cell.styles.overflow = 'linebreak';
        },
        pageBreak: 'auto',
        showHead: 'everyPage',
        showFoot: 'never'
      });
    });

    // Add page numbers if multiple pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
      doc.text(`Page ${i} of ${pageCount}`, pageSize.width - 30, pageHeight - 10);
    }

    // Save the PDF
    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);

    console.log('PDF exported successfully');

  } catch (error) {
    console.error('PDF export error:', error);
    alert('PDF export failed. Please ensure jsPDF and jspdf-autotable are properly installed.');
  }
};

const ManagePayroll = () => {
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [payrolls, setPayrolls] = useState([]);
  const [total, setTotal] = useState(0);
  const [user, setUser] = useState();
  const [showModal, setShowModal] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllowances, setShowAllowances] = useState(true);
  const [selectedAllowances, setSelectedAllowances] = useState({}); // Keyed by payroll ID
  const [availableAllowances, setAvailableAllowances] = useState([]);
  const [loadingAllowances, setLoadingAllowances] = useState(false);
  const [settingAllowances, setSettingAllowances] = useState(false);
  const [storeGroups, setStoreGroups] = useState({}); // Cache store -> group_id mapping
  const [groups, setGroups] = useState([]); // Available groups
  const [selectedGroup, setSelectedGroup] = useState(''); // Selected group ID
  const [groupAllowances, setGroupAllowances] = useState([]); // Allowances for selected group
  const [selectedGroupAllowances, setSelectedGroupAllowances] = useState([]); // Selected allowances for group
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupStores, setGroupStores] = useState([]); // Stores in the selected group
  
  // Check if any store allowances are selected (to disable group selection)
  const hasStoreAllowancesSelected = Object.values(selectedAllowances).some(
    allowances => allowances && allowances.length > 0 && !allowances.some(a => a.isClearAll)
  );
  
  // Check if group allowances are selected (to disable store selection)
  const hasGroupAllowancesSelected = selectedGroupAllowances.length > 0 && 
    !selectedGroupAllowances.some(a => a.isClearAll);
  
  // Special "Clear All" option object
  const CLEAR_ALL_OPTION = { _id: '__CLEAR_ALL__', name: 'Clear All', isClearAll: true };


  // Enhanced table state
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [dense, setDense] = useState(false);
  
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const load = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      setUser(getStoredUser());
      const res = isSearching 
        ? await searchPayrolls(page, rowsPerPage, searchTerm)
        : await getPayrolls(page, rowsPerPage);
      let payrolls = res.data;

      if (getStoredUser()?.company?.trim().toLowerCase() === "sparkle timekeeping demo") {
        payrolls = payrolls.filter(
          (p) => p.store?.trim().toLowerCase() === "sparkletime keepingdemo"
        );
      }
      setPayrolls(payrolls);

      setTotal(res.totalItems || 0);
      if (showLoading) {
        enqueueSnackbar(isSearching ? 'Search completed' : 'Payrolls loaded successfully', { variant: 'success' });
      }
    } catch (err) {
      console.error("Failed to load payrolls:", err);
      const errorMsg = err?.response?.data?.message || 'Failed to load payrolls';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleSearch = async () => {
    const result = await searchPayrolls(page, rowsPerPage, searchTerm)
    setPayrolls(result.data)
    setIsSearching(true);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setIsSearching(false);
    setSearch('');
    setPage(1);
  };

  async function processPayrolls(data) {
    // setLoading(true);
    setProgress(1);

    let processedEmployees = 0;
    const totalEmployees = data.reduce(
      (sum, p) => sum + (p.details?.length || 0),
      0
    );
    const payrollSavePromises = [];

    for (const payroll of data) {
      const dateFrom = new Date(payroll.datefrom).toISOString().split("T")[0];
      const dateTo = new Date(payroll.dateto).toISOString().split("T")[0];

      // sort details alphabetically
      payroll.details.sort((a, b) =>
        a.employeename.toUpperCase().localeCompare(b.employeename.toUpperCase())
      );
      const resolvedDetails = payroll.details.map((detail, index) => {
        const hoursWork =
          typeof detail.hourswork === "string" &&
          detail.hourswork.toLowerCase() === "absent"
            ? 0
            : Math.max(Number(detail.hourswork) || 0, 0);
        const specialHolidayCount = detail.specialholiday === -8 ? 0 : (detail.specialholiday || 0);
        const legalHolidayCount = detail.legalholiday === -8 ? 0 : (detail.legalholiday || 0);
        // Update progress (batch every 10 items for smoother UI)
        processedEmployees++;
        if (processedEmployees % 10 === 0 || processedEmployees === totalEmployees) {
          const percent = Math.round((processedEmployees / totalEmployees) * 100);
          setProgress(percent);
          if (percent === 100) load();
        }
        return {
          employeeid: detail.employeeid,
          employeename: detail.employeename,
          days_work: detail.dayswork,
          hours_work: detail.hourswork,
          hours_tardy: detail.hourstardy,
          overtime: detail.overtime,
          restdays_work: detail.restday,
          special_holiday: specialHolidayCount,
          legal_holiday: legalHolidayCount,
          night_diff: detail.nightdiff,
          other_income_non_tax: 0,
          other_deduction: 0,
          totalLegalNsdHours: detail.totalLegalNsdHours,
          totalLegalOtHours: detail.totalLegalOtHours,
          totalLegalRdHours: detail.totalLegalRdHours,
          totalSpecialNsdHours: detail.totalSpecialNsdHours,
          totalSpecialOtHours: detail.totalSpecialOtHours,
          totalSpecialRdHours: detail.totalSpecialRdHours,
          totalLegalOtNsdHours: detail.totalLegalOtNsdHours,
          totalSpecialOtNsdHours: detail.totalSpecialOtNsdHours,
          hasRemark: detail.hasRemark,
        };
      });
      const validDetails = resolvedDetails.filter((d) => d !== null);
      const payload = {
        store: payroll.store,
        cutoff: payroll.cutoff,
        from: dateFrom,
        to: dateTo,
        employeecount: payroll.employeecount,
        generatedby: isNaN(payroll.generatedby) ? null : payroll.generatedby,
        generatedbyname: isNaN(payroll.generatedby) ? payroll.generatedby : null,
        approvedby: isNaN(payroll.approvedby) ? payroll.approvedby : null,
        breaklistid: payroll.breaklistid,
        createdat: new Date(payroll.createdAt).toISOString().split("T")[0],
        details: validDetails,
      };
      await axios.post("/payroll/save-payroll", payload, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
            "Authorization": `Bearer ${getStoredToken()}`,
        },
      });

      // payrollSavePromises.push(
      //   Promise.all(detailPromises).then((details) => {
      //     const validDetails = details.filter((d) => d !== null);

      //     const payload = {
      //       store: payroll.store,
      //       cutoff: payroll.cutoff,
      //       from: dateFrom,
      //       to: dateTo,
      //       employeecount: payroll.employeecount,
      //       generatedby: isNaN(payroll.generatedby) ? null : payroll.generatedby,
      //       generatedbyname: isNaN(payroll.generatedby)
      //         ? payroll.generatedby
      //         : null,
      //       approvedby: isNaN(payroll.approvedby) ? payroll.approvedby : null,
      //       breaklistid: payroll.breaklistid,
      //       createdat: new Date(payroll.createdAt).toISOString().split("T")[0],
      //       details: validDetails,
      //     };

      //     return axios.post("/payroll/save-payroll", payload, {
      //       headers: {
      //         "Content-Type": "application/json",
      //         "X-CSRF-TOKEN": document
      //           .querySelector('meta[name="csrf-token"]')
      //           ?.getAttribute("content"),
      //       },
      //     });
      //   })
      // );
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await load(false);
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  // Sorting utility functions
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  useEffect(() => {
    load();
  }, [page, rowsPerPage, isSearching]);

  // Load available allowances from masterfiles
  const loadAvailableAllowances = async () => {
    try {
      setLoadingAllowances(true);
      // Get all active allowances (both store and group)
      // Note: We get ALL active allowances, then filter by store/group in getAllowancesForStore
      const response = await getAllowances({ is_active: true });
      if (response.status === 'success') {
        const allowances = response.data || [];
        setAvailableAllowances(allowances);

        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          if (allowances.length > 0) {
          }
        }
      }
    } catch (error) {
      console.error('Error loading allowances:', error);
      enqueueSnackbar('Failed to load allowances', { variant: 'error' });
    } finally {
      setLoadingAllowances(false);
    }
  };

  // Get allowances for a specific store/group
  const getAllowancesForStore = useCallback((storeName, groupId) => {
    if (!availableAllowances.length || !storeName) {
      return [];
    }
    
    // Normalize store name for comparison (trim and lowercase)
    const normalizedStoreName = storeName.trim().toLowerCase();
    
    const filtered = availableAllowances.filter(allowance => {
      if (!allowance.is_active) return false;
      
      // Check if allowance is assigned to the store (case-insensitive)
      // Handle both array and string formats (legacy support)
      let storeAssignments = allowance.assignments?.stores || [];
      if (typeof storeAssignments === 'string') {
        storeAssignments = [storeAssignments];
      }
      if (!Array.isArray(storeAssignments)) {
        storeAssignments = [];
      }
      
      const assignedToStore = storeAssignments.some(store => {
        if (!store) return false;
        return String(store).trim().toLowerCase() === normalizedStoreName;
      });
      
      // Check if allowance is assigned to the group
      // Handle both string and number group IDs, and both array and string formats
      let groupAssignments = allowance.assignments?.groups || [];
      if (typeof groupAssignments === 'string') {
        groupAssignments = [groupAssignments];
      }
      if (!Array.isArray(groupAssignments)) {
        groupAssignments = [];
      }
      
      const assignedToGroup = groupId && groupAssignments.some(group => {
        if (!group) return false;
        // Convert both to string for comparison
        const groupStr = String(group).trim();
        const groupIdStr = String(groupId).trim();
        return groupStr === groupIdStr;
      });
      
      return assignedToStore || assignedToGroup;
    });
    
    
    return filtered;
  }, [availableAllowances]);

  // Get allowances assigned to store only (from store masterfile). Used for per-row allowances dropdown.
  // Excludes group-only allowances; when applying store allowances, group allowances are overwritten.
  const getStoreOnlyAllowances = useCallback((storeName) => {
    if (!availableAllowances.length || !storeName) {
      return [];
    }
    const normalizedStoreName = storeName.trim().toLowerCase();
    return availableAllowances.filter(allowance => {
      if (!allowance.is_active) return false;
      let storeAssignments = allowance.assignments?.stores || [];
      if (typeof storeAssignments === 'string') storeAssignments = [storeAssignments];
      if (!Array.isArray(storeAssignments)) storeAssignments = [];
      return storeAssignments.some(store =>
        store && String(store).trim().toLowerCase() === normalizedStoreName
      );
    });
  }, [availableAllowances]);

  // Load store group mappings
  useEffect(() => {
    const loadStoreGroups = async () => {
      try {
        const response = await listStores();
        if (response?.status === 'success') {
          const mapping = {};
          response.data.stores.forEach(store => {
            if (store.store && store.group_id) {
              // Store both original and normalized key for lookup
              mapping[store.store] = String(store.group_id).trim();
            }
          });
          setStoreGroups(mapping);
        }
      } catch (error) {
        console.error('Error loading store groups:', error);
        // Only show error if it's not an auth error (to avoid spam)
        if (error?.response?.status !== 401) {
          enqueueSnackbar('Failed to load store information', { variant: 'error' });
        }
      }
    };
    
    if (showAllowances && payrolls.length > 0) {
      loadStoreGroups();
    }
  }, [showAllowances, payrolls.length, enqueueSnackbar]);

  // Load allowances when showAllowances is checked or when payrolls change
  useEffect(() => {
    if (showAllowances && availableAllowances.length === 0) {
      loadAvailableAllowances();
    }
  }, [showAllowances]);

  // Initialize selectedAllowances with applied store allowances from payroll.allowances
  useEffect(() => {
    if (payrolls.length > 0 && availableAllowances.length > 0) {
      setSelectedAllowances(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        payrolls.forEach(payroll => {
          // Only initialize if not already set by user
          if (!prev[payroll._id] && payroll.allowances && Array.isArray(payroll.allowances) && payroll.allowances.length > 0) {
            const storeName = payroll.store || '';
            const normalizedStoreName = storeName.trim().toLowerCase();
            
            // Get store-only allowances for this payroll's store
            const storeOnlyAllowances = availableAllowances.filter(allowance => {
              if (!allowance.is_active) return false;
              let storeAssignments = allowance.assignments?.stores || [];
              if (typeof storeAssignments === 'string') storeAssignments = [storeAssignments];
              if (!Array.isArray(storeAssignments)) storeAssignments = [];
              return storeAssignments.some(store =>
                store && String(store).trim().toLowerCase() === normalizedStoreName
              );
            });
            const storeAllowanceIds = new Set(storeOnlyAllowances.map(a => a._id?.toString()));
            
            // Map applied allowances to match available allowances by _id, but only store allowances
            const appliedAllowances = payroll.allowances
              .map(appliedAllowance => {
                // Only preselect if it's a store allowance
                if (!storeAllowanceIds.has(appliedAllowance._id?.toString())) {
                  return null;
                }
                
                // Find matching allowance from availableAllowances
                const matchingAllowance = availableAllowances.find(
                  avail => avail._id?.toString() === appliedAllowance._id?.toString()
                );
                if (matchingAllowance) {
                  return matchingAllowance;
                }
                // If not found in availableAllowances, use the applied allowance data
                return {
                  _id: appliedAllowance._id,
                  name: appliedAllowance.name || 'Unknown',
                  amount: appliedAllowance.amount || 0
                };
              })
              .filter(Boolean); // Remove any undefined/null values
            
            if (appliedAllowances.length > 0) {
              updated[payroll._id] = appliedAllowances;
              hasChanges = true;
            }
          }
        });
        
        return hasChanges ? updated : prev;
      });
    }
  }, [payrolls, availableAllowances]);

  // Load groups that user has access to
  const loadGroups = async () => {
    try {
      setLoadingGroups(true);
      const user = getStoredUser();
      const userGroupAccess = user?.groupAccess || [];
      
      
      const response = await listGroups();
      
      if (response.status === 'success' && response.data?.groups) {
        const allGroups = response.data.groups;
        
        // Filter groups based on user's groupAccess
        let availableGroups = [];
        
        if (userGroupAccess.length > 0) {
          // If user has groupAccess, filter groups
          availableGroups = allGroups.filter(group => {
            const groupId = (group.id?.toString() || group._id?.toString() || '').trim();
            const groupName = (group.name || '').trim();
            
            // Check if group ID or name matches user's groupAccess
            const matches = userGroupAccess.some(access => {
              const accessStr = String(access).trim().toLowerCase();
              const groupIdLower = groupId.toLowerCase();
              const groupNameLower = groupName.toLowerCase();
              
              return accessStr === groupIdLower || 
                     accessStr === groupNameLower ||
                     groupIdLower.includes(accessStr) ||
                     groupNameLower.includes(accessStr);
            });
            
            if (matches) {
              console.log(`Group matched: ${groupName} (ID: ${groupId})`);
            }
            
            return matches;
          });
          
          // If filtering resulted in no groups, show all groups as fallback
          if (availableGroups.length === 0) {
            console.warn('No groups matched groupAccess, showing all groups as fallback');
            availableGroups = allGroups;
          }
          

        } else {
          // If no groupAccess restrictions, show all groups
          availableGroups = allGroups;
          
        }
        
        setGroups(availableGroups);
      } else {
        console.warn('Unexpected response structure:', response);
        setGroups([]);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      enqueueSnackbar('Failed to load groups', { variant: 'error' });
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Load groups on mount
  useEffect(() => {
    if (showAllowances) {
      loadGroups();
    }
  }, [showAllowances]);

  // When group is selected, filter allowances for that group.
  // Match by group id OR group name: backend/group masterfile may store assignments.groups as id or name (e.g. "7STAR - DWK").
  useEffect(() => {
    if (selectedGroup && availableAllowances.length > 0) {
      const groupIdStr = String(selectedGroup).trim();
      const selectedGroupObj = groups.find(
        g => (g.id?.toString() || g._id?.toString() || '').trim() === groupIdStr
      );
      const selectedGroupName = (selectedGroupObj?.name || '').trim();

      const filtered = availableAllowances.filter(allowance => {
        if (!allowance.is_active) return false;

        let groupAssignments = allowance.assignments?.groups || [];
        if (typeof groupAssignments === 'string') {
          groupAssignments = [groupAssignments];
        }
        if (!Array.isArray(groupAssignments)) {
          groupAssignments = [];
        }

        return groupAssignments.some(group => {
          if (!group) return false;
          const g = String(group).trim();
          const matchById = g === groupIdStr;
          const matchByName = selectedGroupName && g.toLowerCase() === selectedGroupName.toLowerCase();
          return matchById || matchByName;
        });
      });
      setGroupAllowances(filtered);
    } else {
      setGroupAllowances([]);
      setSelectedGroupAllowances([]);
      if (!selectedGroup) {
        setGroupStores([]);
      }
    }
  }, [selectedGroup, availableAllowances, groups]);

  // Handle setting allowances to payrolls
  const handleSetAllowances = async () => {
    // Check if Clear All is selected in group mode
    const hasClearAllInGroup = selectedGroupAllowances.some(a => a.isClearAll);
    
    if (selectedGroup && hasClearAllInGroup) {
      try {
        setSettingAllowances(true);
        
        // Use cached group stores or fetch for dialog text and "no stores" check only
        let storesToUse = groupStores;
        if (storesToUse.length === 0) {
          const groupStoresResponse = await listGroupStores(selectedGroup);
          if (groupStoresResponse.status !== 'success' || !groupStoresResponse.data?.stores) {
            throw new Error('Failed to fetch stores for the selected group');
          }
          storesToUse = groupStoresResponse.data.stores.map(s => s.store);
          setGroupStores(storesToUse);
        }
        
        if (storesToUse.length === 0) {
          enqueueSnackbar('No stores found in the selected group', { variant: 'warning' });
          return;
        }
        
        const confirmed = await Swal.fire({
          title: 'Clear Group Allowances',
          text: `Clear group allowances from all payrolls in the selected group (${storesToUse.length} store(s))? Store and personnel allowances will be preserved. The backend will process every store in the group that has payrolls.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Clear Group Allowances',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#d33'
        });
        
        if (!confirmed.isConfirmed) return;
        
        // Backend resolves payrolls from group stores; no search-payrolls needed
        const response = await clearAllowancesFromPayrolls([], 'group', { groupId: selectedGroup });
        
        if (response.status !== 'success') {
          throw new Error(response.message || 'Failed to clear allowances');
        }
        
        const payrollsProcessed = response.data?.payrolls_processed ?? 0;
        const results = response.data?.results ?? [];
        enqueueSnackbar(response.message || `Group allowances cleared from ${payrollsProcessed} payroll(s)`, { variant: 'success' });
        results.forEach(r => r.payroll_id && invalidatePayrollCache(r.payroll_id));
        await load(false);
        setSelectedGroupAllowances([]);
        setSelectedGroup('');
      } catch (error) {
        console.error('Error clearing group allowances:', error);
        const errorMsg = error?.response?.data?.message || 'Failed to clear allowances';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      } finally {
        setSettingAllowances(false);
      }
      return;
    }
    
    // Check if Clear All is selected in individual mode
    const allIndividualAllowances = Object.values(selectedAllowances).flat();
    const hasClearAllInIndividual = allIndividualAllowances.some(a => a.isClearAll);
    
    if (hasClearAllInIndividual) {
      // Get payroll IDs that have "Clear All" selected
      const payrollIdsToClear = Object.keys(selectedAllowances).filter(
        payrollId => selectedAllowances[payrollId]?.some(a => a.isClearAll)
      );
      
      if (payrollIdsToClear.length === 0) {
        enqueueSnackbar('No payrolls selected for clearing', { variant: 'warning' });
        return;
      }
      
      const confirmed = await Swal.fire({
        title: 'Clear Store Allowances',
        text: `Clear store allowances from ${payrollIdsToClear.length} selected payroll(s)? Personnel allowances will be preserved.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Clear Store Allowances',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33'
      });
      
      if (!confirmed.isConfirmed) return;
      
      try {
        setSettingAllowances(true);
        // Clear only store allowances from selected payrolls (preserves personnel allowances)
        const response = await clearAllowancesFromPayrolls(payrollIdsToClear, 'store');
        
        if (response.status !== 'success') {
          throw new Error(response.message || 'Failed to clear allowances');
        }
        
        enqueueSnackbar(`Store allowances cleared successfully from ${payrollIdsToClear.length} payroll(s)`, { variant: 'success' });
        // Invalidate cache for all affected payrolls
        payrollIdsToClear.forEach(payrollId => invalidatePayrollCache(payrollId));
        await load(false);
        
        // Remove Clear All from selections and clear other selections for those payrolls
        setSelectedAllowances(prev => {
          const updated = { ...prev };
          payrollIdsToClear.forEach(payrollId => {
            delete updated[payrollId];
          });
          return updated;
        });
      } catch (error) {
        console.error('Error clearing allowances:', error);
        const errorMsg = error?.response?.data?.message || 'Failed to clear allowances';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      } finally {
        setSettingAllowances(false);
      }
      return;
    }
    
    // Check if group mode is active (normal assignment)
    if (selectedGroup && selectedGroupAllowances.length > 0) {
      // Group mode: backend resolves payrolls from group stores (no search-payrolls)
      try {
        setSettingAllowances(true);
        
        // Filter out Clear All option
        const actualAllowances = selectedGroupAllowances.filter(a => !a.isClearAll);
        if (actualAllowances.length === 0) {
          enqueueSnackbar('Please select at least one allowance (excluding Clear All)', { variant: 'warning' });
          return;
        }
        
        const allowanceIds = actualAllowances.map(a => a._id);
        
        const confirmed = await Swal.fire({
          title: 'Set All Allowances (Group)',
          text: `Assign ${actualAllowances.length} allowance(s) to all payrolls in the selected group? The backend will apply to every store in the group that has payrolls.`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Set All',
          cancelButtonText: 'Cancel'
        });
        
        if (!confirmed.isConfirmed) return;
        
        // Backend resolves payrolls from group_id; no payroll_ids or search-payrolls needed
        const response = await assignAllowancesToPayrolls(allowanceIds, [], { allowanceType: 'group', groupId: selectedGroup });
        
        if (response.status !== 'success') {
          throw new Error(response.message || 'Failed to assign allowances');
        }
        
        const payrollsProcessed = response.data?.payrolls_processed ?? 0;
        const results = response.data?.results ?? [];
        enqueueSnackbar(response.message || `Allowances assigned to ${payrollsProcessed} payroll(s)`, { variant: 'success' });
        results.forEach(r => r.payroll_id && invalidatePayrollCache(r.payroll_id));
        await load(false);
        setSelectedGroupAllowances([]);
        setSelectedGroup('');
      } catch (error) {
        console.error('Error setting group allowances:', error);
        const errorMsg = error?.response?.data?.message || 'Failed to assign allowances';
        enqueueSnackbar(errorMsg, { variant: 'error' });
      } finally {
        setSettingAllowances(false);
      }
      return;
    }
    
    // Individual payroll mode: apply to all payrolls for stores that have selected allowances
    // Collect all selected allowances from all payrolls (excluding Clear All)
    const allIndividualAllowancesFiltered = Object.values(selectedAllowances)
      .flat()
      .filter(a => !a.isClearAll);
    const uniqueAllowanceIds = [...new Set(allIndividualAllowancesFiltered.map(a => a._id))];
    
    if (uniqueAllowanceIds.length === 0) {
      enqueueSnackbar('Please select at least one allowance', { variant: 'warning' });
      return;
    }

    const payrollIdsWithAllowances = Object.keys(selectedAllowances).filter(
      payrollId => {
        const allowances = selectedAllowances[payrollId] || [];
        // Check if there are actual allowances (not just Clear All)
        return allowances.some(a => !a.isClearAll);
      }
    );

    if (payrollIdsWithAllowances.length === 0) {
      enqueueSnackbar('Please select allowances for at least one payroll', { variant: 'warning' });
      return;
    }

    // Get stores from selected payrolls on current page
    const selectedPayrolls = payrolls.filter(p => payrollIdsWithAllowances.includes(p._id));
    const storeNames = [...new Set(selectedPayrolls.map(p => p.store))];
    
    // Get the allowance IDs that are common across all selected payrolls
    // Or use the most common allowances selected
    const allowanceCounts = {};
    payrollIdsWithAllowances.forEach(payrollId => {
      const allowances = (selectedAllowances[payrollId] || []).filter(a => !a.isClearAll);
      allowances.forEach(a => {
        allowanceCounts[a._id] = (allowanceCounts[a._id] || 0) + 1;
      });
    });
    
    // Use all unique allowances that were selected
    const allowanceIdsToApply = uniqueAllowanceIds;

    // Fetch ALL payroll IDs for these stores (across all pages)
    const allPayrollIdsForStores = await getAllPayrollIdsByStores(storeNames);

    if (allPayrollIdsForStores.length === 0) {
      enqueueSnackbar('No payrolls found for the selected stores', { variant: 'warning' });
      return;
    }

    const confirmed = await Swal.fire({
      title: 'Set All Allowances',
      text: `Assign ${allowanceIdsToApply.length} allowance(s) to ${allPayrollIdsForStores.length} payroll(s) across ${storeNames.length} store(s)? This will apply to ALL payrolls for these stores, not just the current page.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Set All',
      cancelButtonText: 'Cancel'
    });

    if (!confirmed.isConfirmed) return;

    try {
      setSettingAllowances(true);
      
      // Call API to assign allowances to all payrolls for these stores.
      // replaceGroupAllowances: overwrite group-assigned allowances with store allowances.
      // allowanceType: 'store' - only process store allowances (optimization)
      const response = await assignAllowancesToPayrolls(
        allowanceIdsToApply,
        allPayrollIdsForStores,
        { replaceGroupAllowances: true, allowanceType: 'store' }
      );

      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to assign allowances');
      }

      enqueueSnackbar(`Allowances assigned successfully to ${allPayrollIdsForStores.length} payroll(s)`, { variant: 'success' });
      // Invalidate cache for all affected payrolls
      allPayrollIdsForStores.forEach(payrollId => invalidatePayrollCache(payrollId));
      await load(false);
      setSelectedAllowances({});
    } catch (error) {
      console.error('Error setting allowances:', error);
      const errorMsg = error?.response?.data?.message || 'Failed to assign allowances';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setSettingAllowances(false);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  async function fetchHolidays() {
    try {
      const res = await axios.get("/holidays", {
        headers: {
          "X-CSRF-TOKEN": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
        responseType: "text", // because Laravel is sending HTML
      });

      const parser = new DOMParser();
      const doc = parser.parseFromString(res.data, "text/html");

      const holidays = [];
      doc.querySelectorAll("tr").forEach((row) => {
        const cols = row.querySelectorAll("td");
        if (cols.length >= 3) {
          const holiday = {
            date: cols[0].textContent.trim(),
            name: cols[1].textContent.trim(),
            type: cols[2].textContent.trim(),
          };
          if (holiday.date && holiday.name && holiday.type) {
            holidays.push(holiday);
          }
        }
      });

      return holidays;
    } catch (err) {
      console.error("Failed to fetch holidays:", err);
      throw err;
    }
  }

  // async function fetchRates(detail) {
  //   try {
  //     const { employeeid, store } = detail;
  //     const response = await axios.post(
  //       `/payroll/rates-stk`,
  //       {
  //         employeeId: detail.employeeid,
  //         store: detail.store            
  //       },
  //       {
  //         headers: {
  //           "X-CSRF-TOKEN": document
  //             .querySelector('meta[name="csrf-token"]')
  //             ?.getAttribute("content"),
  //             "Authorization": `Bearer ${getStoredToken()}`,
  //         },
  //       }
  //     );

  //     return response.data; // same as your old resolve(data)
  //   } catch (error) {
  //     console.error("Error fetching rates:", error);
  //     throw error; // same as reject(error)
  //   }
  // }

  const formatCurrency = (amount) =>
    Number(amount || 0).toLocaleString("en-PH", {
      style: "currency",
      currency: "PHP",
      minimumFractionDigits: 2,
    });

  const getCompanyDisplay = (storeName) => {
    if (!storeName) return "N/A";
    const normalizedStore = String(storeName).toLowerCase();
    if (normalizedStore.includes("syzygy")) return "Syzygy Staffing";
    if (normalizedStore.includes("seven star jasem")) return "JASEM Service";
    if (/7[- ]star manpower/.test(normalizedStore)) return "7-Star Manpower";
    return "Inhouse";
  };

  const getMasterfileAllowances = (payroll) => {
    const allAllowances = payroll?.allowances || [];
    return allAllowances.filter((allowance) => {
      if (!allowance.source && allowance.user_id != null) return true;
      return allowance.source === 'personnel' || allowance.source === 'store' || allowance.source === 'group';
    });
  };

  const getTotalBasicPayValue = (payroll) =>
    payroll?.informations?.reduce((sum, info) => sum + (Number(info.basic_pay) || 0), 0) || 0;

  const getTotalAllowanceValue = (payroll) => {
    const allAllowances = payroll?.allowances || [];
    const informations = payroll?.informations || [];
    const masterfileAllowances = allAllowances.filter((allowance) => {
      let source = allowance.source || null;
      if (!source && allowance.user_id) {
        source = 'personnel';
      }
      return source === 'personnel' || source === 'store' || source === 'group';
    });

    const personnelAllowances = masterfileAllowances.filter((allowance) => allowance.user_id);
    const storeGroupAllowances = masterfileAllowances.filter((allowance) => !allowance.user_id);
    const employeeIdsWithPersonnelAllowance = new Set(
      personnelAllowances.map((allowance) => String(allowance.user_id)).filter(Boolean)
    );

    const totalPersonnelAllowance = personnelAllowances.reduce((sum, allowance) => {
      const allowanceAmount = Number(allowance?.amount || 0);
      const userId = String(allowance.user_id);
      const employeeInfo = informations.find((info) => String(info.employee_id) === userId);
      const hoursWorked = Number(employeeInfo?.hours_work ?? employeeInfo?.hours_worked ?? 0);
      return sum + (allowanceAmount * hoursWorked);
    }, 0);

    const totalStoreGroupAllowance = storeGroupAllowances.reduce((sum, allowance) => {
      const allowanceAmount = Number(allowance?.amount || 0);
      const totalHoursForEligibleEmployees = informations.reduce((hoursSum, info) => {
        const employeeId = String(info.employee_id);
        if (employeeIdsWithPersonnelAllowance.has(employeeId)) {
          return hoursSum;
        }
        const hoursWorked = Number(info.hours_work ?? info.hours_worked ?? 0);
        return hoursSum + hoursWorked;
      }, 0);

      return sum + (allowanceAmount * totalHoursForEligibleEmployees);
    }, 0);

    return totalPersonnelAllowance + totalStoreGroupAllowance;
  };

  const getAllowanceDetailsDisplay = (payroll) => {
    const masterfileAllowances = getMasterfileAllowances(payroll);
    if (masterfileAllowances.length === 0) return "-";

    return masterfileAllowances.map((allowance) => {
      let sourceLabel = 'Store/Group';
      if (allowance.user_id || allowance.source === 'personnel') {
        sourceLabel = 'Personnel';
      } else if (allowance.source === 'store') {
        sourceLabel = 'Store';
      } else if (allowance.source === 'group') {
        sourceLabel = 'Group';
      }

      return `${allowance.name} (${sourceLabel}) ${formatCurrency(allowance.amount)}`;
    }).join(' | ');
  };

  const getSelectedAllowancesDisplay = (payroll) => {
    if (!showAllowances) return "";

    const selected = selectedAllowances[payroll._id];
    if (selected && selected.length > 0) {
      return selected.map((allowance) => allowance?.name || "-").join(", ");
    }

    const storeAllowances = getStoreOnlyAllowances(payroll.store || '') || [];
    const storeAllowanceIds = new Set(storeAllowances.map((allowance) => allowance._id?.toString()));
    const appliedAllowances = (payroll.allowances || []).filter((allowance) =>
      storeAllowanceIds.has(allowance._id?.toString())
    );

    return appliedAllowances.length > 0
      ? appliedAllowances.map((allowance) => allowance?.name || "-").join(", ")
      : "-";
  };

  const getContributionDetailsDisplay = (payroll) => {
    const sss = payroll?.informations?.reduce((acc, info) => acc + (Number(info.sss_contribution) || 0), 0) || 0;
    const hdmf = payroll?.informations?.reduce((acc, info) => acc + (Number(Math.max(0, info.hdmf_contribution ?? 0)) || 0), 0) || 0;
    const nhip = payroll?.informations?.reduce((acc, info) => acc + (Number(info.nhip_contribution) || 0), 0) || 0;
    return `SSS: ${formatCurrency(sss)} | HDMF: ${formatCurrency(hdmf)} | NHIP: ${formatCurrency(nhip)}`;
  };

  const buildPayrollExportRows = (items) => items.map((payroll) => {
    const row = {
      Company: getCompanyDisplay(payroll.store),
      Store: payroll.store || "-",
    };

    if (!isMobile) {
      row["Generated By"] = payroll.generated_by_name ?? payroll.generated_by ?? "-";
      row["Approved By"] = payroll.approved_by || "-";
      row["Approved Date"] = payroll?.date_generated ? dayjs(payroll.date_generated).format("MMM DD, YYYY") : "-";
    }

    row["Period"] = payroll?.payroll_from && payroll?.payroll_to
      ? `${dayjs(payroll.payroll_from).format("MMM DD")} - ${dayjs(payroll.payroll_to).format("MMM DD, YYYY")}`
      : "-";
    row["Created Date"] = payroll?.date_generated ? `Created: ${dayjs(payroll.date_generated).format("MMM DD, YYYY")}` : "Created: -";
    row["Cutoff"] = payroll.cutoff ?? "-";
    row["Employee Count"] = payroll?.informations?.length ?? 0;

    if (showAllowances) {
      row["Select Allowances"] = getSelectedAllowancesDisplay(payroll);
    }

    row["Total Basic Pay"] = payroll.source === "System Generated"
      ? "Please Set Rates"
      : formatCurrency(getTotalBasicPayValue(payroll));

    if (showAllowances) {
      const totalAllowance = getTotalAllowanceValue(payroll);
      row["Total Allowance"] = totalAllowance > 0 ? formatCurrency(totalAllowance) : "-";
    }

    row["Allowance Details"] = getAllowanceDetailsDisplay(payroll);

    if (!isMobile) {
      row["Contributions"] = getContributionDetailsDisplay(payroll);
    }

    return row;
  });

  const handleExportCSV = () => {
      if (!filteredPayrolls || filteredPayrolls.length === 0) {
        alert("No data to export");
        return;
      }

      const exportRows = buildPayrollExportRows(filteredPayrolls);
      const headers = Object.keys(exportRows[0]);
      const csvRows = [
        headers.join(","),
        ...exportRows.map(row =>
          headers.map(field => JSON.stringify(row[field] ?? "")).join(",")
        ),
      ];

      const csvContent = "\uFEFF" + csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "payroll_transformed.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    };

  const getStoreDailyRate = async (storeName) => {
    const response = await getRates({ id: storeName });
    const rates = response?.data || {};
    return Number(rates.daily ?? rates.dailyRate ?? 0);
  };

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const getStoresWithInvalidDailyRates = async (payrollRows = []) => {
    const uniqueStores = Array.from(
      new Set(
        payrollRows
          .map((payroll) => String(payroll.store || '').trim())
          .filter(Boolean)
      )
    );

    const checkedStores = await Promise.all(
      uniqueStores.map(async (storeName) => {
        const dailyRate = await getStoreDailyRate(storeName);
        return { storeName, dailyRate };
      })
    );

    return checkedStores.filter(({ dailyRate }) => !Number.isFinite(dailyRate) || dailyRate <= 0);
  };

  const handleConfirm = async () => {
    if (!fromDate) {
      alert("Please select date.");
      return;
    }
    setShowModal(false);
    const result = await Swal.fire({
      title: "Confirm Update",
      text: `Update Records for the date ${fromDate}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      setProgress(1);
      // const nextDay = new Date(fromDate);
      // nextDay.setDate(nextDay.getDate() + 1);
      const userId = user?.id || 1; 
      const response = await getApprovedBreaklists({
        payroll: userId,
        from: fromDate,
        to: fromDate,
        storeAccess: user?.storeAccess || [],
        groupAccess: user?.groupAccess || []
      });

      if (!response.success) throw new Error(response.message || "Failed to fetch breaklists");

      const newPayrolls = response.data;
      if(newPayrolls.length <= 0) {
        Swal.fire("Success", "No records found!", "success");
      }
      else {
          // 2. Batch check existing payrolls
          const payrollsToCheck = newPayrolls.map((p) => ({
            payroll_from: new Date(p.datefrom).toISOString().split("T")[0],
            payroll_to: new Date(p.dateto).toISOString().split("T")[0],

            store: p.store.trim().toLowerCase(),
          }));

          const batchRes = await axios.post("/payroll/batch-check-existing-payrolls", payrollsToCheck, {
            headers: { "Content-Type": "application/json" },
          });

          const nonExistingPayrolls = batchRes.data.data?.non_existing_payrolls || [];
          const filteredPayrolls = newPayrolls.filter((p) => {
            const dateFrom = new Date(p.datefrom).toISOString().split("T")[0];
            const dateTo = new Date(p.dateto).toISOString().split("T")[0];
            const storeName = p.store.trim().toLowerCase();
            const generatedBy = p.generatedby;
            const approvedBy = p.approvedby;
            return nonExistingPayrolls.some(
              (n) => n.store === storeName && n.payroll_from === dateFrom && n.payroll_to === dateTo
            );
          });
          const storesWithInvalidRates = await getStoresWithInvalidDailyRates(filteredPayrolls);

          if (storesWithInvalidRates.length > 0) {
            const storeItems = storesWithInvalidRates
              .map(({ storeName }) => storeName)
              .sort((left, right) => left.localeCompare(right))
              .map((storeName) => `<li>${escapeHtml(storeName)}</li>`)
              .join("");

            Swal.fire({
              title: "Store Rates Required",
              icon: "warning",
              html: `
                <div style="text-align: left;">
                  <p style="margin: 0 0 10px;">
                    Update Records cannot continue because these stores have a daily rate of 0:
                  </p>
                  <ul style="margin: 0 0 12px 20px; padding: 0;">
                    ${storeItems}
                  </ul>
                  <p style="margin: 0;">
                    Please set the correct store rates, then run Update Records again.
                  </p>
                </div>
              `,
              confirmButtonText: "OK"
            });
            return;
          }

          let processed = 0;
          await processPayrolls(filteredPayrolls)
          // for (const payroll of filteredPayrolls) {
            
          //   processed++;
          //   setProgress(Math.round((processed / filteredPayrolls.length) * 100));

          //   // const holidays = await fetchHolidays();
          //   // console.log("Holidays:", holidays);
          // }

          Swal.fire("Success", "All payrolls saved successfully!", "success"); 
      }
      
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update records", "error");
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const onClose = () => {
  	setShowModal(false)
  }

  // OTP Dialog handlers
  const handleEditClick = (payrollId) => {
    // Invalidate cache to ensure fresh data when editing
    invalidatePayrollCache(payrollId);
    navigate(`/payroll/${payrollId}/edit`);
  };

  const filteredPayrolls = isSearching ? payrolls : payrolls.filter(
    (payroll) =>
      payroll.store.toLowerCase().includes(search.toLowerCase())
  );
    const exportRows = useMemo(() => buildPayrollExportRows(filteredPayrolls), [filteredPayrolls, isMobile, selectedAllowances, showAllowances]);
  const dataToExport = formatDataForExport(exportRows, "payroll_details");
  
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="300px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="200px" height={20} sx={{ mb: 3 }} />
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="300px" height={36} />
          </Box>
          {[...Array(8)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReceiptIcon sx={{ color: '#12C1DC' }} />
              Manage Payroll
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{ 
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        {/* Breadcrumbs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/payroll">
              Payroll
            </Link>
            <Typography color="text.primary">Manage Payroll</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Progress Bar for Updates */}
        {progress > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Processing payrolls... {progress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#12C1DC'
                  }
                }}
              />
            </Paper>
          </motion.div>
        )}

        {/* Enhanced Payroll Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            {/* Table Header */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151', mb: 2 }}>
                Payroll Records
              </Typography>
              
              {/* Enhanced Controls */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', lg: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', lg: 'center' },
                gap: 2
              }}>
                {/* Left Controls */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2, 
                  alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                  <Button 
                    variant="contained" 
                    startIcon={<UpdateIcon />}
                    onClick={() => setShowModal(true)}
                    disabled={isLoading}
                    sx={{
                      bgcolor: '#10b981',
                      '&:hover': { bgcolor: '#059669' },
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Update Records
                  </Button>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      Show
                    </Typography>
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(e.target.value);
                        setPage(1);
                      }}
                      size="small"
                      sx={{ minWidth: 80 }}
                    >
                      {[5, 10, 25, 50].map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                      entries
                    </Typography>
                  </Box>
                </Box>

                {/* Right Controls */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search payrolls..."
                      variant="outlined"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#9ca3af', fontSize: '1.1rem' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ 
                        minWidth: { xs: 'auto', md: '250px' },
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#12C1DC' },
                          '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSearch}
                      disabled={isLoading}
                      sx={{
                        bgcolor: '#12C1DC',
                        '&:hover': { bgcolor: '#0FABDB' },
                        textTransform: 'none',
                        minWidth: '80px',
                        height: '40px'
                      }}
                    >
                      Search
                    </Button>
                    {isSearching && (
                      <Button
                        variant="outlined"
                        onClick={handleClearSearch}
                        disabled={isLoading}
                        sx={{
                          borderColor: '#12C1DC',
                          color: '#12C1DC',
                          '&:hover': { borderColor: '#0FABDB', bgcolor: 'rgba(18, 193, 220, 0.1)' },
                          textTransform: 'none',
                          minWidth: '80px',
                          height: '40px'
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showAllowances}
                        onChange={(e) => setShowAllowances(e.target.checked)}
                        sx={{
                          color: '#12C1DC',
                          '&.Mui-checked': {
                            color: '#12C1DC'
                          }
                        }}
                      />
                    }
                    label="Show Allowances"
                    sx={{ ml: 1 }}
                  />
                  
                  {showAllowances && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Select Group</InputLabel>
                        <Select
                          value={selectedGroup}
                          onChange={async (e) => {
                            const newGroupId = e.target.value;
                            setSelectedGroup(newGroupId);
                            setSelectedGroupAllowances([]);
                            
                            // Clear store selections when switching to group mode
                            if (newGroupId) {
                              setSelectedAllowances({});
                            }
                            
                            // If a group is selected, fetch its stores and clear individual selections
                            if (newGroupId) {
                              try {
                                const groupStoresResponse = await listGroupStores(newGroupId);
                                if (groupStoresResponse.status === 'success' && groupStoresResponse.data?.stores) {
                                  const stores = groupStoresResponse.data.stores.map(s => s.store);
                                  setGroupStores(stores);
                                  
                                  // Clear selections for payrolls belonging to stores in this group
                                  setSelectedAllowances(prev => {
                                    const updated = { ...prev };
                                    payrolls.forEach(payroll => {
                                      if (stores.includes(payroll.store)) {
                                        delete updated[payroll._id];
                                      }
                                    });
                                    return updated;
                                  });
                                }
                              } catch (error) {
                                console.error('Error fetching group stores:', error);
                              }
                            } else {
                              // Group deselected, clear group stores
                              setGroupStores([]);
                            }
                          }}
                          label="Select Group"
                          disabled={loadingGroups || hasStoreAllowancesSelected}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e5e7eb'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#12C1DC'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#12C1DC'
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>None (Individual Selection)</em>
                          </MenuItem>
                          {groups.length === 0 ? (
                            <MenuItem disabled>
                              {loadingGroups ? 'Loading...' : 'No groups available'}
                            </MenuItem>
                          ) : (
                            groups.map((group) => {
                              const groupId = group.id?.toString() || group._id?.toString();
                              const groupName = group.name || 'Unnamed Group';
                              return (
                                <MenuItem key={groupId} value={groupId}>
                                  {groupName}
                                </MenuItem>
                              );
                            })
                          )}
                        </Select>
                      </FormControl>
                      
                      {selectedGroup && (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          size="small"
                          options={[CLEAR_ALL_OPTION, ...groupAllowances]}
                          getOptionLabel={(option) => option.name || ''}
                          value={selectedGroupAllowances}
                          onChange={(event, newValue) => {
                            // If Clear All is selected, clear other selections
                            const hasClearAll = newValue.some(a => a.isClearAll);
                            if (hasClearAll) {
                              setSelectedGroupAllowances([CLEAR_ALL_OPTION]);
                            } else {
                              setSelectedGroupAllowances(newValue);
                            }
                          }}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          loading={loadingAllowances}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Checkbox
                                checked={selectedGroupAllowances.some(a => a._id === option._id)}
                                sx={{ mr: 1 }}
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: option.isClearAll ? '#d32f2f' : 'inherit',
                                  fontWeight: option.isClearAll ? 600 : 'normal'
                                }}
                              >
                                {option.name}
                              </Typography>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={groupAllowances.length === 0 ? "No allowances available" : "Select allowances..."}
                              helperText={groupAllowances.length === 0 ? "No allowances assigned to this group" : ""}
                              sx={{
                                minWidth: 250,
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': { borderColor: '#12C1DC' },
                                  '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                                }
                              }}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={option._id}
                                label={option.name}
                                {...getTagProps({ index })}
                                size="small"
                                sx={{
                                  bgcolor: option.isClearAll ? '#ffebee' : '#e3f2fd',
                                  color: option.isClearAll ? '#d32f2f' : '#1976d2',
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  fontWeight: option.isClearAll ? 600 : 'normal',
                                  '& .MuiChip-deleteIcon': {
                                    color: option.isClearAll ? '#d32f2f' : '#1976d2',
                                    fontSize: '1rem'
                                  }
                                }}
                              />
                            ))
                          }
                        />
                      )}
                      
                      <Button
                        variant="contained"
                        onClick={handleSetAllowances}
                        disabled={
                          settingAllowances || 
                          (selectedGroup 
                            ? selectedGroupAllowances.length === 0
                            : Object.values(selectedAllowances).flat().filter(a => !a.isClearAll).length === 0 && 
                              !Object.values(selectedAllowances).some(arr => arr.some(a => a.isClearAll)))
                        }
                        sx={{
                          bgcolor: (selectedGroup && selectedGroupAllowances.some(a => a.isClearAll)) || 
                                   Object.values(selectedAllowances).some(arr => arr.some(a => a.isClearAll))
                            ? '#d32f2f' : '#12C1DC',
                          '&:hover': { 
                            bgcolor: (selectedGroup && selectedGroupAllowances.some(a => a.isClearAll)) || 
                                     Object.values(selectedAllowances).some(arr => arr.some(a => a.isClearAll))
                              ? '#c62828' : '#0FABDB' 
                          },
                          textTransform: 'none',
                          minWidth: '100px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {settingAllowances ? (
                          <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                          (selectedGroup && selectedGroupAllowances.some(a => a.isClearAll)) || 
                          Object.values(selectedAllowances).some(arr => arr.some(a => a.isClearAll))
                            ? 'Clear All' 
                            : 'Set All'
                        )}
                      </Button>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Export CSV">
                      <IconButton 
                        size="small"
                        onClick={handleExportCSV}
                        sx={{ 
                          border: '1px solid #e5e7eb', 
                          borderRadius: 2, 
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <ExportIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>
                   <Tooltip title="Print">
                     <IconButton 
                       size="small"
                       onClick={() => exportToPrint(dataToExport, 'Payroll Employee Details Report')}
                       sx={{ 
                         border: '1px solid #e5e7eb', 
                         borderRadius: 2, 
                         '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                       }}
                     >
                       <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                     </IconButton>
                   </Tooltip>
                    <Tooltip title="Export PDF">
                      <IconButton 
                        size="small"
                        onClick={() => exportToPDF(dataToExport, "Payroll Report", 'Payroll Details Report')}
                        sx={{ 
                          border: '1px solid #e5e7eb', 
                          borderRadius: 2, 
                          '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                        }}
                      >
                        <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Enhanced Table */}
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Table stickyHeader size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      whiteSpace: 'nowrap'
                    }}>
                      Actions
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Company
                      </TableCell>
                    )}
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Store
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Generated By
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Approved By
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          bgcolor: '#f9fafb',
                          color: '#374151',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Approved Date
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Period
                    </TableCell>
                   <TableCell align="center" sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Employee Count
                    </TableCell>
                    {showAllowances && (
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '250px'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                          <span>Select Allowances</span>
                          <Tooltip title="Deselect all preselected store allowances">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                disabled={hasGroupAllowancesSelected || !Object.keys(selectedAllowances).length}
                                onClick={() => setSelectedAllowances({})}
                                sx={{ textTransform: 'none', minWidth: 'auto', py: 0.25, px: 1 }}
                              >
                                Deselect all
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Total Basic Pay
                    </TableCell>
                    {showAllowances && (
                      <TableCell align="right" sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '120px'
                      }}>
                        Total Allowance
                      </TableCell>
                    )}
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '200px'
                      }}>
                        Allowance Details
                      </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Contributions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {filteredPayrolls.map((p, index) => {


                      const sss = p.informations?.reduce((acc, i) => acc + (i.sss_contribution || 0), 0) || 0;
                      const hdmf = p.informations?.reduce(
                        (acc, i) => acc + (Number(Math.max(0, i.hdmf_contribution ?? 0)) || 0),
                        0
                      ) || 0;
                      const nhip = p.informations?.reduce((acc, i) => acc + (i.nhip_contribution || 0), 0) || 0;

                      const isEvenRow = index % 2 === 0;
                      return (
                        <motion.tr
                          key={p._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          style={{
                            display: 'table-row',
                            backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important'
                            },
                            '& > .MuiTableCell-root': {
                              borderRight: '1px solid #e0e0e0',
                              borderBottom: '1px solid #e0e0e0'
                            },
                            '& > .MuiTableCell-root:last-child': {
                              borderRight: 'none'
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Tooltip title="Edit payroll">
                                <IconButton
                                  size="small"
                                  onMouseEnter={() => prefetchPayrollById(p._id)}
                                  onClick={() => handleEditClick(p._id)}
                                  sx={{
                                    color: '#12C1DC',
                                    '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Delete payroll">
                                <IconButton
                                  size="small"
                                  disabled={deletingId === p._id}
                                  onClick={async () => {
                                    const confirmed = await Swal.fire({
                                      title: 'Delete Payroll',
                                      text: 'Are you sure you want to delete this payroll? This action cannot be undone.',
                                      icon: 'warning',
                                      showCancelButton: true,
                                      confirmButtonText: 'Delete',
                                      cancelButtonText: 'Cancel'
                                    });
                                    if (!confirmed.isConfirmed) return;
                                    try {
                                      setDeletingId(p._id);
                                      await deletePayroll(p._id);
                                      setPayrolls((prev) => prev.filter(x => x._id !== p._id));
                                      enqueueSnackbar('Payroll deleted successfully', { variant: 'success' });
                                    } catch (err) {
                                      console.error(err);
                                      enqueueSnackbar('Failed to delete payroll', { variant: 'error' });
                                    } finally {
                                      setDeletingId(null);
                                    }
                                  }}
                                  sx={{ 
                                    color: '#ef4444',
                                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                  }}
                                >
                                  {deletingId === p._id ? (
                                    <CircularProgress size={16} sx={{ color: '#ef4444' }} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Save payroll">
                                <IconButton
                                  size="small"
                                  disabled={savingId === p._id}
                                  onClick={async () => {
                                    const confirmed = await Swal.fire({
                                      title: 'Save Payroll',
                                      text: 'Mark this payroll as saved?',
                                      icon: 'question',
                                      showCancelButton: true,
                                      confirmButtonText: 'Save',
                                      cancelButtonText: 'Cancel'
                                    });
                                    if (!confirmed.isConfirmed) return;
                                    try {
                                      setSavingId(p._id);
                                      await savePayroll(p._id);
                                      await load(false);
                                      enqueueSnackbar('Payroll saved successfully', { variant: 'success' });
                                    } catch (err) {
                                      console.error(err);
                                      enqueueSnackbar('Failed to save payroll', { variant: 'error' });
                                    } finally {
                                      setSavingId(null);
                                    }
                                  }}
                                  sx={{ 
                                    color: '#f59e0b',
                                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.1)' }
                                  }}
                                >
                                  {savingId === p._id ? (
                                    <CircularProgress size={16} sx={{ color: '#f59e0b' }} />
                                  ) : (
                                    <SaveIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BusinessIcon sx={{ color: '#6b7280', fontSize: '1rem' }} />
                                <Typography variant="body2" fontWeight={500}>
                                  {p.store
                                    ? p.store.toLowerCase().includes("syzygy")
                                      ? "Syzygy Staffing"
                                      : p.store.toLowerCase().includes("seven star jasem")
                                      ? "JASEM Service"
                                      : /7[- ]star manpower/.test(p.store.toLowerCase())
                                      ? "7-Star Manpower"
                                      : "Inhouse"
                                    : "N/A"}
                                </Typography>
                              </Box>
                            </TableCell>
                          )}
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StoreIcon sx={{ color: '#12C1DC', fontSize: '1rem' }} />
                              <Box>
                                <Tooltip title={p.store}>
                                  <Typography variant="body2" fontWeight={600} sx={{ 
                                    maxWidth: { xs: '120px', md: '200px' },
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {p.store || "-"}
                                  </Typography>
                                </Tooltip>
                                {isMobile && (
                                  <Typography variant="caption" color="text.secondary">
                                    {p.company || "N/A"}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          
                          {!isMobile && (
                            <>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PersonIcon sx={{ color: '#6b7280', fontSize: '1rem' }} />
                                  <Typography variant="body2">
                                    {p.generated_by_name ?? p.generated_by ?? "-"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {p.approved_by}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(p.date_generated).format("MMM DD, YYYY")}
                                </Typography>
                              </TableCell>
{/*                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PersonIcon sx={{ color: '#6b7280', fontSize: '1rem' }} />
                                  <Typography variant="body2">
                                    {p.approved_by || "-"}
                                  </Typography>
                                </Box>
                              </TableCell>*/}
                            </>
                          )}
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {dayjs(p.payroll_from).format("MMM DD")} - {dayjs(p.payroll_to).format("MMM DD, YYYY")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Created: {dayjs(p.date_generated).format("MMM DD, YYYY")}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            {p.cutoff}
                          </TableCell>


                          <TableCell align="center">
                            {p.informations.length}
                          </TableCell>
                          
                          {showAllowances && (() => {
                            const storeAllowances = getStoreOnlyAllowances(p.store || '') || [];
                            // Check if this payroll's store belongs to the selected group
                            const isStoreInSelectedGroup = selectedGroup && groupStores.includes(p.store);
                            // Disable store selection if group allowances are selected
                            const isDisabled = isStoreInSelectedGroup || hasGroupAllowancesSelected;
                            
                            // Get applied allowances for this payroll to pre-select them (store-only for display)
                            const storeAllowanceIds = new Set(storeAllowances.map(a => a._id?.toString()));
                            const appliedAllowances = (p.allowances || [])
                              .map(appliedAllowance => {
                                const matchingAllowance = availableAllowances.find(
                                  avail => avail._id?.toString() === appliedAllowance._id?.toString()
                                );
                                return matchingAllowance || {
                                  _id: appliedAllowance._id,
                                  name: appliedAllowance.name || 'Unknown',
                                  amount: appliedAllowance.amount || 0
                                };
                              })
                              .filter(Boolean)
                              .filter(a => storeAllowanceIds.has(a._id?.toString()));
                            
                            // Merge applied allowances with selected allowances, prioritizing selected (store-only)
                            const currentValue = isStoreInSelectedGroup 
                              ? [] 
                              : (selectedAllowances[p._id] || appliedAllowances);
                            
                            // Add Clear All option to the list
                            const optionsWithClearAll = [CLEAR_ALL_OPTION, ...storeAllowances];
                            
                            return (
                              <TableCell>
                                <Autocomplete
                                  multiple
                                  disableCloseOnSelect
                                  size="small"
                                  disabled={isDisabled}
                                  options={optionsWithClearAll}
                                  getOptionLabel={(option) => option.name || ''}
                                  value={currentValue}
                                  onChange={(event, newValue) => {
                                    if (isStoreInSelectedGroup || hasGroupAllowancesSelected) {
                                      return;
                                    }
                                    
                                    // Clear group selection when store allowances are selected
                                    if (newValue.length > 0 && !newValue.some(a => a.isClearAll)) {
                                      setSelectedGroup('');
                                      setSelectedGroupAllowances([]);
                                      setGroupStores([]);
                                    }
                                    
                                    // If Clear All is selected, clear other selections
                                    const hasClearAll = newValue.some(a => a.isClearAll);
                                    if (hasClearAll) {
                                      setSelectedAllowances(prev => ({
                                        ...prev,
                                        [p._id]: [CLEAR_ALL_OPTION]
                                      }));
                                    } else {
                                      setSelectedAllowances(prev => ({
                                        ...prev,
                                        [p._id]: newValue
                                      }));
                                    }
                                  }}
                                  isOptionEqualToValue={(option, value) => option._id === value._id}
                                  loading={loadingAllowances}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                      <Checkbox
                                        checked={currentValue.some(a => a._id === option._id)}
                                        sx={{ mr: 1 }}
                                      />
                                      <Typography 
                                        variant="body2"
                                        sx={{ 
                                          color: option.isClearAll ? '#d32f2f' : 'inherit',
                                          fontWeight: option.isClearAll ? 600 : 'normal'
                                        }}
                                      >
                                        {option.name}
                                      </Typography>
                                    </Box>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={
                                        isStoreInSelectedGroup 
                                          ? "Group mode active" 
                                          : storeAllowances.length === 0 
                                            ? "No allowances available" 
                                            : "Select..."
                                      }
                                      helperText={
                                        isStoreInSelectedGroup 
                                          ? "Use group selection above" 
                                          : storeAllowances.length === 0 && availableAllowances.length > 0 
                                            ? "No allowances assigned to this store" 
                                            : ""
                                      }
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          '&:hover fieldset': { borderColor: isDisabled ? '#e0e0e0' : '#12C1DC' },
                                          '&.Mui-focused fieldset': { borderColor: isDisabled ? '#e0e0e0' : '#12C1DC' },
                                          '&.Mui-disabled': {
                                            bgcolor: '#f5f5f5',
                                            cursor: 'not-allowed'
                                          }
                                        }
                                      }}
                                    />
                                  )}
                                renderTags={(value, getTagProps) =>
                                  value.map((option, index) => (
                                    <Chip
                                      key={option._id}
                                      label={option.name}
                                      {...getTagProps({ index })}
                                      size="small"
                                      sx={{
                                        bgcolor: option.isClearAll ? '#ffebee' : '#e3f2fd',
                                        color: option.isClearAll ? '#d32f2f' : '#1976d2',
                                        fontSize: '0.7rem',
                                        height: '20px',
                                        fontWeight: option.isClearAll ? 600 : 'normal',
                                        '& .MuiChip-deleteIcon': {
                                          color: option.isClearAll ? '#d32f2f' : '#1976d2',
                                          fontSize: '1rem'
                                        }
                                      }}
                                    />
                                  ))
                                }
                                  sx={{ minWidth: '200px', maxWidth: '300px' }}
                                />
                              </TableCell>
                            );
                          })()}
                          
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#059669' }}>
                                {p.source === "System Generated"
                                  ? "Please Set Rates"
                                  : (
                                      p?.informations?.reduce((sum, info) => {
                                        return sum + (info.basic_pay || 0);
                                      }, 0)
                                    ).toLocaleString("en-PH", {
                                      style: "currency",
                                      currency: "PHP",
                                      minimumFractionDigits: 2,
                                    })}
                              </Typography>
                            </Box>
                          </TableCell>
                          {showAllowances && (
                            <TableCell align="right">
                              {(() => {
                                const allAllowances = p.allowances || [];
                                const informations = p.informations || [];
                                
                                // Helper to check if allowance is from masterfile
                                const isMasterfileAllowance = (source) => {
                                  return source === 'personnel' || source === 'store' || source === 'group';
                                };
                                
                                // Filter to only masterfile allowances (personnel, store, group)
                                // Exclude additional income allowances
                                const masterfileAllowances = allAllowances.filter(a => {
                                  let source = a.source || null;
                                  // If no source but has user_id, it's personnel
                                  if (!source && a.user_id) {
                                    source = 'personnel';
                                  }
                                  return isMasterfileAllowance(source);
                                });
                                
                                // Get personnel allowances (those with user_id and masterfile source)
                                const personnelAllowances = masterfileAllowances.filter(a => a.user_id);
                                
                                // Get store/group allowances (those without user_id)
                                const storeGroupAllowances = masterfileAllowances.filter(a => !a.user_id);
                                
                                // Get unique employee IDs that have personnel allowances
                                const employeeIdsWithPersonnelAllowance = new Set(
                                  personnelAllowances.map(pa => String(pa.user_id)).filter(Boolean)
                                );
                                
                                // Calculate total from personnel allowances: allowance * hours worked for matching employee
                                const totalPersonnelAllowance = personnelAllowances.reduce((sum, allowance) => {
                                  const allowanceAmount = Number(allowance?.amount || 0);
                                  const userId = String(allowance.user_id);
                                  
                                  // Find the employee info to get their hours worked
                                  const employeeInfo = informations.find(info => 
                                    String(info.employee_id) === userId
                                  );
                                  const hoursWorked = employeeInfo?.hours_work ?? employeeInfo?.hours_worked ?? 0;
                                  
                                  return sum + (allowanceAmount * hoursWorked);
                                }, 0);
                                
                                // Calculate total from store/group allowances
                                // For each store/group allowance, calculate for employees that DON'T have 
                                // ANY personnel allowance: allowance * hours worked for each eligible employee
                                const totalStoreGroupAllowance = storeGroupAllowances.reduce((sum, allowance) => {
                                  const allowanceAmount = Number(allowance?.amount || 0);
                                  
                                  // Sum hours worked for eligible employees (those without personnel allowance)
                                  const totalHoursForEligibleEmployees = informations.reduce((hoursSum, info) => {
                                    const employeeId = String(info.employee_id);
                                    // Skip employees who have personnel allowances
                                    if (employeeIdsWithPersonnelAllowance.has(employeeId)) {
                                      return hoursSum;
                                    }
                                    const hoursWorked = info.hours_work ?? info.hours_worked ?? 0;
                                    return hoursSum + hoursWorked;
                                  }, 0);
                                  
                                  return sum + (allowanceAmount * totalHoursForEligibleEmployees);
                                }, 0);

                                const grandTotalAllowance = totalPersonnelAllowance + totalStoreGroupAllowance;
                                
                                return grandTotalAllowance > 0 ? (
                                  <Typography variant="body2" fontWeight={500} sx={{ color: '#059669' }}>
                                    ₱{grandTotalAllowance.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                          )}
                            <TableCell>
                              {(() => {
                                const allAllowances = p.allowances || [];
                                
                                // Helper to check if allowance is from masterfile
                                const isMasterfile = (source, hasUserId) => {
                                  if (!source && hasUserId) return true; // Personnel
                                  return source === 'personnel' || source === 'store' || source === 'group';
                                };
                                
                                // Filter to only masterfile allowances
                                const masterfileAllowances = allAllowances.filter(a => 
                                  isMasterfile(a.source, a.user_id != null)
                                );
                                
                                if (masterfileAllowances.length === 0) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      -
                                    </Typography>
                                  );
                                }
                                
                                return (
                                  <Box sx={{ fontSize: '0.75rem' }}>
                                    {masterfileAllowances.map((allowance, idx) => {
                                      // Determine source label and color
                                      let sourceLabel = '';
                                      let sourceColor = 'text.secondary';
                                      
                                      if (allowance.user_id || allowance.source === 'personnel') {
                                        sourceLabel = 'Personnel';
                                        sourceColor = '#9333ea'; // Purple
                                      } else if (allowance.source === 'store') {
                                        sourceLabel = 'Store';
                                        sourceColor = '#2563eb'; // Blue
                                      } else if (allowance.source === 'group') {
                                        sourceLabel = 'Group';
                                        sourceColor = '#059669'; // Green
                                      } else {
                                        // Default for allowances without source (legacy data)
                                        sourceLabel = 'Store/Group';
                                        sourceColor = '#6b7280'; // Gray
                                      }
                                      
                                      const amount = Number(allowance.amount || 0);
                                      
                                      return (
                                        <Box key={allowance._id || idx} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                            {allowance.name}
                                          </Typography>
                                          <Typography 
                                            variant="caption" 
                                            sx={{ 
                                              color: sourceColor, 
                                              fontSize: '0.65rem',
                                              bgcolor: `${sourceColor}15`,
                                              px: 0.5,
                                              py: 0.1,
                                              borderRadius: 0.5
                                            }}
                                          >
                                            {sourceLabel}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: '#059669', fontWeight: 500 }}>
                                            ₱{amount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ fontSize: '0.75rem' }}>
                                <Typography variant="caption" display="block">
                                  <strong>SSS:</strong> ₱{sss.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>HDMF:</strong> ₱{hdmf.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>NHIP:</strong> ₱{nhip.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                                </Typography>
                              </Box>
                            </TableCell>
                          )}
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Enhanced Pagination */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 2,
              borderTop: '1px solid #e5e7eb',
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                Showing {(page - 1) * rowsPerPage + 1} to{" "}
                {Math.min(page * rowsPerPage, total)} of {total} entries
                {isSearching && ` (search results for "${searchTerm}")`}
                {!isSearching && search && ` (showing ${filteredPayrolls.length} filtered results)`}
              </Typography>

              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => handleChangePage(newPage)}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      backgroundColor: '#12C1DC',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0FABDB'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </motion.div>

        <DateRangeModal
          open={showModal}
          onClose={onClose}
          onConfirm={handleConfirm}
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          // setToDate={setToDate}
        />

      </Box>
    </Fade>
  );
};

export default ManagePayroll;

